const STORAGE_KEY = "omnimedia.screenCredential";

/**
 * The screen credential the shared API client should send as a bearer token.
 * This is `null` everywhere except inside the active player runtime — the
 * unified app also serves the Clerk-authenticated control center, which relies
 * on session cookies and MUST NOT have a bearer token attached.
 */
let activeScreenCredential: string | null = null;

export function getActiveScreenCredential(): string | null {
  return activeScreenCredential;
}

export function setActiveScreenCredential(credential: string | null): void {
  activeScreenCredential = credential;
}

export function readStoredCredential(): string | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && stored.length > 0) return stored;
  } catch {
    // localStorage may be unavailable (private mode); fall through.
  }
  return null;
}

export function persistCredential(credential: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, credential);
  } catch {
    // Best effort; the in-memory credential still drives this session.
  }
}

export function clearCredential(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore.
  }
}

/** URL-fragment key the desktop shell uses to hand a screen credential over. */
const FRAGMENT_CREDENTIAL_KEY = "screen_credential";

/**
 * Adopt a screen credential handed to the player window by the desktop shell in
 * the URL **fragment** (never the query — fragments are not sent to the server
 * or written to access logs), then strip it from the URL.
 *
 * This must run BEFORE React renders (see main.tsx) so both DeviceProvider and
 * PlayerEntry read it from storage on their first pass. It is **authoritative
 * over any previously-persisted credential**: the desktop hub and player run on
 * different origins, and a "change device" re-claim reloads the SAME player
 * window with a new token here — that new token must win over the old one still
 * in this origin's localStorage, or the box would keep playing the old device.
 *
 * Returns the adopted token, or null if there was no fragment credential.
 */
export function adoptFragmentCredential(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const hash = window.location.hash.replace(/^#/, "");
    if (!hash) return null;
    const params = new URLSearchParams(hash);
    const token = params.get(FRAGMENT_CREDENTIAL_KEY);
    if (!token) return null;
    params.delete(FRAGMENT_CREDENTIAL_KEY);
    persistCredential(token);
    setActiveScreenCredential(token);
    // Strip only our param; preserve any other fragment state.
    const rest = params.toString();
    const url = `${window.location.pathname}${window.location.search}${rest ? `#${rest}` : ""}`;
    window.history.replaceState(window.history.state, "", url);
    return token;
  } catch {
    return null;
  }
}
